import { Mesh as BABYLON_Mesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/mesh.js';
import { AbstractMesh as BABYLON_AbstractMesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/abstractMesh.js';
import { TransformNode as BABYLON_TransformNode } from '@inter-ikea-betrakta-babylonjs/core/Meshes/transformNode.js';
import { AssetContainer as BABYLON_AssetContainer } from '@inter-ikea-betrakta-babylonjs/core/assetContainer.js';
import { RenderingGroupID } from '../Viewer.js';
import { SceneAssetMesh } from './SceneAssetMesh.js';
import { SceneAssetMeshInstance } from './SceneAssetMeshInstance.js';
import { Camera as BABYLON_Camera } from '@inter-ikea-betrakta-babylonjs/core/Cameras/camera.js';
import { InstancedMesh as BABYLON_InstancedMesh } from '@inter-ikea-betrakta-babylonjs/core/Meshes/instancedMesh.js';
import { Vector3 as BABYLON_Vector3, Quaternion as BABYLON_Quaternion } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { PBRMaterial as BABYLON_PBRMaterial } from '@inter-ikea-betrakta-babylonjs/core/Materials/PBR/pbrMaterial.js';
import { BetraktaPBRMaterial } from '../material/BetraktaPBRMaterial.js';
import { Logger } from '../../logging/Logger.js';
const LOGGER_CATEGORY = "SceneAssetContainer";
export class SceneAssetContainer {
    constructor(viewer, babylon_container) {
        this.meshes = [];
        this.mesh_node_to_scene_mesh = new WeakMap();
        this.flattened_instances = [];
        this.babylon_container = babylon_container;
        if (viewer.get_optional_features().feature_gate_use_betrakta_material_shaders) {
            // Translate BABYLON_PBRMaterial to Betrakta PBR materials.
            // We won't consider non-PBR materials.
            // AFAIK, This can't be done in the GLTF loader extension due to Babylon requiring a BABYLON_PBRMaterial
            const material_map = new Map();
            if (babylon_container instanceof BABYLON_AssetContainer) {
                for (let i = 0; i < babylon_container.materials.length; i++) {
                    const babylon_material = babylon_container.materials[i];
                    if (!(babylon_material instanceof BABYLON_PBRMaterial))
                        continue;
                    if (material_map.has(babylon_material)) {
                        babylon_container.materials[i] = material_map.get(babylon_material).babylon_material;
                        continue;
                    }
                    const betrakta_material = new BetraktaPBRMaterial(viewer, babylon_material);
                    material_map.set(babylon_material, betrakta_material);
                    babylon_container.materials[i] = betrakta_material.babylon_material;
                }
            }
            this.iterate_meshes((mesh) => {
                const babylon_material = mesh.material;
                if (!(babylon_material instanceof BABYLON_PBRMaterial))
                    return;
                if (material_map.has(babylon_material)) {
                    mesh.material = material_map.get(babylon_material).babylon_material;
                    return;
                }
                const betrakta_material = new BetraktaPBRMaterial(viewer, babylon_material);
                material_map.set(babylon_material, betrakta_material);
                mesh.material = betrakta_material.babylon_material;
            });
        }
        this.iterate_meshes((mesh) => {
            mesh.renderingGroupId = RenderingGroupID.DEFAULT_RENDERING_GROUP;
            if (mesh instanceof BABYLON_Mesh) {
                const instance_mesh = new SceneAssetMesh(viewer, mesh);
                this.meshes.push(instance_mesh);
                this.mesh_node_to_scene_mesh.set(mesh, instance_mesh);
            }
        });
        this.iterate_meshes((mesh) => {
            if (mesh instanceof BABYLON_Mesh || mesh instanceof BABYLON_InstancedMesh) {
                const source_mesh = (mesh instanceof BABYLON_Mesh) ? mesh : mesh.sourceMesh;
                if (source_mesh.geometry === null)
                    return;
                const asset_mesh = this.mesh_node_to_scene_mesh.get(source_mesh);
                const instance = {
                    position: new BABYLON_Vector3(),
                    scale: new BABYLON_Vector3(),
                    rotation: new BABYLON_Quaternion(),
                    mesh: asset_mesh
                };
                const flattened_transform = mesh.computeWorldMatrix(true);
                flattened_transform.decompose(instance.scale, instance.rotation, instance.position);
                this.flattened_instances.push(instance);
            }
        });
        this.iterate_asset_container_root_nodes((root) => {
            root.setEnabled(false);
        });
    }
    iterate_asset_container_root_nodes(func) {
        if (Array.isArray(this.babylon_container)) {
            this.babylon_container.forEach(func);
        }
        else {
            this.babylon_container.rootNodes.forEach(func);
        }
    }
    get_container_materials() {
        if (Array.isArray(this.babylon_container)) {
            const materials_set = new Set();
            this.iterate_meshes((mesh) => {
                if (mesh.material !== null) {
                    materials_set.add(mesh.material);
                }
            });
            const materials = [];
            materials_set.forEach((material) => materials.push(material));
            return materials;
        }
        else {
            return this.babylon_container.materials;
        }
    }
    iterate_meshes(func) {
        this.iterate_asset_container_root_nodes((root_node) => {
            const meshes = root_node.getChildMeshes(false);
            if (root_node instanceof BABYLON_AbstractMesh) {
                func(root_node);
            }
            meshes.forEach(func);
        });
    }
    sync_instance(instance, flatten) {
        instance.dispose_mesh();
        instance.animation_groups = null;
        const created_asset_mesh_instances = [];
        if (this.babylon_container !== null) {
            if (flatten) {
                this.flattened_instances.forEach((source_instance) => {
                    const mesh_instance = new SceneAssetMeshInstance(instance, source_instance.mesh);
                    mesh_instance.babylon_instance.position.copyFrom(source_instance.position);
                    mesh_instance.babylon_instance.scaling.copyFrom(source_instance.scale);
                    mesh_instance.babylon_instance.rotationQuaternion = source_instance.rotation.clone();
                    mesh_instance.babylon_instance.parent = instance.root;
                    if (this.should_freeze_instances()) {
                        mesh_instance.babylon_instance.computeWorldMatrix(true);
                        mesh_instance.babylon_instance.freezeWorldMatrix();
                    }
                    created_asset_mesh_instances.push(mesh_instance);
                });
            }
            else { // Clone hierarchy
                const source_to_instance_map = new Map();
                this.iterate_asset_container_root_nodes((root) => {
                    const all_children = root.getChildren(undefined, false);
                    const new_root = this.clone_babylon_node_for_asset_instance(root, created_asset_mesh_instances, instance);
                    new_root.parent = instance.root;
                    source_to_instance_map.set(root, new_root);
                    all_children.forEach((child_node) => {
                        const clone = this.clone_babylon_node_for_asset_instance(child_node, created_asset_mesh_instances, instance);
                        source_to_instance_map.set(child_node, clone);
                    });
                    // Assign new parents
                    source_to_instance_map.forEach((new_node, source_node) => {
                        new_node.setEnabled(true);
                        new_node.markAsDirty('');
                        if (source_node.parent !== null) {
                            const found_parent = source_to_instance_map.get(source_node.parent);
                            if (found_parent === undefined) {
                                throw new Error('Illegal parent found.');
                            }
                            new_node.parent = found_parent;
                        }
                        else {
                            new_node.parent = instance.root;
                        }
                        if (this.should_freeze_instances() && new_node instanceof BABYLON_TransformNode) {
                            new_node.computeWorldMatrix(true);
                            new_node.freezeWorldMatrix();
                        }
                    });
                });
                if (this.babylon_container instanceof BABYLON_AssetContainer) {
                    // Clone the animation groups and remap the target nodes.
                    const animation_groups = this.babylon_container.animationGroups.map(animation_group => {
                        const new_animation_group = animation_group.clone(animation_group.name, (old) => {
                            const new_node = source_to_instance_map.get(old);
                            return new_node;
                        });
                        return new_animation_group;
                    });
                    instance.animation_groups = animation_groups;
                }
            }
        }
        instance.on_new_meshes(created_asset_mesh_instances);
    }
    should_freeze_instances() {
        return !this.has_animations();
    }
    has_animations() {
        return this.babylon_container instanceof BABYLON_AssetContainer && this.babylon_container.animationGroups.length > 0;
    }
    clone_babylon_node_for_asset_instance(source_node, created_asset_mesh_instances, scene_asset_instance) {
        let clone;
        if (source_node instanceof BABYLON_Mesh) {
            if (source_node.geometry === null) {
                return new BABYLON_TransformNode('empty mesh placeholder', source_node._scene);
            }
            const asset_mesh = this.mesh_node_to_scene_mesh.get(source_node);
            if (asset_mesh === undefined) {
                Logger.as(LOGGER_CATEGORY).error('Internal error: missing asset mesh with name ' + source_node.name);
                return new BABYLON_TransformNode('missing mesh placeholder', source_node._scene);
            }
            const asset_mesh_instance = new SceneAssetMeshInstance(scene_asset_instance, asset_mesh);
            asset_mesh_instance.babylon_instance.parent = null;
            asset_mesh_instance.babylon_instance.position = source_node.position.clone();
            asset_mesh_instance.babylon_instance.scaling = source_node.scaling.clone();
            if (source_node.rotationQuaternion !== null) {
                asset_mesh_instance.babylon_instance.rotationQuaternion = source_node.rotationQuaternion.clone();
            }
            else {
                asset_mesh_instance.babylon_instance.rotationQuaternion = source_node.rotation.toQuaternion();
            }
            asset_mesh_instance.babylon_instance.scalingDeterminant = source_node.scalingDeterminant;
            created_asset_mesh_instances.push(asset_mesh_instance);
            return asset_mesh_instance.babylon_instance;
        }
        else if (source_node instanceof BABYLON_InstancedMesh) {
            const asset_mesh = this.mesh_node_to_scene_mesh.get(source_node.sourceMesh);
            if (asset_mesh === undefined) {
                Logger.as(LOGGER_CATEGORY).error('missing instance asset mesh: ' + source_node.name);
                return new BABYLON_TransformNode('missing mesh placeholder', source_node._scene);
            }
            const asset_mesh_instance = new SceneAssetMeshInstance(scene_asset_instance, asset_mesh);
            asset_mesh_instance.babylon_instance.parent = null;
            asset_mesh_instance.babylon_instance.position = source_node.position.clone();
            asset_mesh_instance.babylon_instance.scaling = source_node.scaling.clone();
            if (source_node.rotationQuaternion !== null) {
                asset_mesh_instance.babylon_instance.rotationQuaternion = source_node.rotationQuaternion.clone();
            }
            else {
                asset_mesh_instance.babylon_instance.rotationQuaternion = source_node.rotation.toQuaternion();
            }
            asset_mesh_instance.babylon_instance.scalingDeterminant = source_node.scalingDeterminant;
            asset_mesh_instance.mark_variant_key_dirty();
            created_asset_mesh_instances.push(asset_mesh_instance);
            return asset_mesh_instance.babylon_instance;
        }
        else if (source_node instanceof BABYLON_AbstractMesh) {
            Logger.as(LOGGER_CATEGORY).error('Unexpected mesh type: ' + source_node.name + '(' + source_node.getClassName() + ')');
            clone = source_node.clone('bad mesh type placeholder', null, true);
        }
        else if (source_node instanceof BABYLON_TransformNode) {
            clone = source_node.clone('transform clone', null, true);
        }
        else if (source_node instanceof BABYLON_Camera) {
            return new BABYLON_TransformNode('camera placeholder', source_node._scene);
        }
        else {
            Logger.as(LOGGER_CATEGORY).error('Unexpected babylon node type: ' + source_node.getClassName());
            clone = new BABYLON_TransformNode('unknown node placeholder', source_node._scene);
        }
        if (clone === null) {
            clone = new BABYLON_TransformNode('failed clone placeholder', source_node._scene);
        }
        return clone;
    }
    dispose() {
        this.meshes.forEach((mesh) => {
            mesh.dispose();
        });
        this.flattened_instances.length = 0;
        if (Array.isArray(this.babylon_container)) {
            this.babylon_container.forEach((mesh) => {
                mesh.dispose(true, false);
            });
            this.babylon_container.length = 0;
        }
        else {
            this.babylon_container.dispose();
        }
    }
}
//# sourceMappingURL=SceneAssetContainer.js.map